/**
 * Drawing the icons that sit on top of blocks and cells: one shape per
 * palette color, so colors can be told apart by shape as well as color (see
 * palette.ts for why).
 *
 * Canvas drawing works in two stages: first describe a "path" (an outline,
 * using calls like `moveTo`, `lineTo` and `arc`), then fill it. Each shape
 * below builds a path centered on (`cx`, `cy`) that fits inside a circle of
 * radius `r`, so all icons come out the same size.
 */
import type { Shape, Swatch, Theme } from "./palette";

/** Draw one icon, filled with the current fill color, centered on (`cx`, `cy`) and fitting within radius `r`. */
export function drawShape(
	ctx: CanvasRenderingContext2D,
	shape: Shape,
	cx: number,
	cy: number,
	r: number,
): void {
	ctx.beginPath();
	switch (shape) {
		case "circle":
			ctx.arc(cx, cy, r * 0.9, 0, Math.PI * 2);
			break;
		case "square":
			ctx.rect(cx - r * 0.78, cy - r * 0.78, r * 1.56, r * 1.56);
			break;
		case "triangle":
			polygon(ctx, cx, cy + r * 0.15, r * 1.05, 3, -Math.PI / 2);
			break;
		case "diamond":
			polygon(ctx, cx, cy, r, 4, -Math.PI / 2);
			break;
		case "star":
			star(ctx, cx, cy, r, r * 0.45, 5);
			break;
		case "plus": {
			const t = r * 0.36;
			ctx.rect(cx - r, cy - t, r * 2, t * 2);
			ctx.rect(cx - t, cy - r, t * 2, r * 2);
			ctx.fill("nonzero");
			return;
		}
		case "ring":
			ctx.arc(cx, cy, r * 0.9, 0, Math.PI * 2);
			ctx.moveTo(cx + r * 0.5, cy);
			ctx.arc(cx, cy, r * 0.5, 0, Math.PI * 2);
			ctx.fill("evenodd");
			return;
		case "heart":
			heart(ctx, cx, cy, r);
			break;
		case "hexagon":
			polygon(ctx, cx, cy, r * 0.95, 6, 0);
			break;
		case "drop":
			drop(ctx, cx, cy, r);
			break;
		case "moon":
			moon(ctx, cx, cy, r);
			return;
		case "hourglass":
			points(ctx, cx, cy, r, [
				[-0.8, -0.9],
				[0.8, -0.9],
				[0.12, 0],
				[0.8, 0.9],
				[-0.8, 0.9],
				[-0.12, 0],
			]);
			break;
		case "cross": {
			// An X: a plus sign rotated by 45 degrees.
			const t = r * 0.34;
			const arm = r * 1.05;
			ctx.save();
			ctx.translate(cx, cy);
			ctx.rotate(Math.PI / 4);
			ctx.rect(-arm, -t, arm * 2, t * 2);
			ctx.rect(-t, -arm, t * 2, arm * 2);
			ctx.restore();
			ctx.fill("nonzero");
			return;
		}
		case "half":
			ctx.arc(cx, cy + r * 0.35, r * 0.95, Math.PI, Math.PI * 2);
			ctx.closePath();
			break;
		case "bolt":
			points(ctx, cx, cy, r, [
				[0.2, -1],
				[-0.6, 0.12],
				[-0.05, 0.12],
				[-0.25, 1],
				[0.6, -0.18],
				[0.05, -0.18],
			]);
			break;
		case "pentagon":
			polygon(ctx, cx, cy + r * 0.06, r, 5, -Math.PI / 2);
			break;
		case "frame":
			// A hollow square: an outer and an inner outline. With the "evenodd"
			// fill rule, the area inside both is left unfilled.
			ctx.rect(cx - r * 0.8, cy - r * 0.8, r * 1.6, r * 1.6);
			ctx.rect(cx - r * 0.38, cy - r * 0.38, r * 0.76, r * 0.76);
			ctx.fill("evenodd");
			return;
		case "outlineTriangle":
			polygon(ctx, cx, cy + r * 0.2, r * 1.1, 3, -Math.PI / 2);
			polygon(ctx, cx, cy + r * 0.2, r * 0.48, 3, -Math.PI / 2);
			ctx.fill("evenodd");
			return;
		case "outlineDiamond":
			polygon(ctx, cx, cy, r * 1.05, 4, -Math.PI / 2);
			polygon(ctx, cx, cy, r * 0.48, 4, -Math.PI / 2);
			ctx.fill("evenodd");
			return;
		case "arrow":
			points(ctx, cx, cy, r, [
				[-0.95, -0.3],
				[0.1, -0.3],
				[0.1, -0.8],
				[0.95, 0],
				[0.1, 0.8],
				[0.1, 0.3],
				[-0.95, 0.3],
			]);
			break;
		case "bar":
			ctx.roundRect(cx - r * 0.95, cy - r * 0.4, r * 1.9, r * 0.8, r * 0.4);
			break;
		case "clover":
			// Three overlapping discs and a stem. Each disc starts with `moveTo`
			// so no stray line joins it to the previous one.
			for (const [x, y] of [
				[0, -0.42],
				[-0.45, 0.12],
				[0.45, 0.12],
			] as const) {
				ctx.moveTo(cx + (x + 0.4) * r, cy + y * r);
				ctx.arc(cx + x * r, cy + y * r, r * 0.4, 0, Math.PI * 2);
			}
			ctx.rect(cx - r * 0.1, cy, r * 0.2, r * 0.95);
			ctx.fill("nonzero");
			return;
		case "spade":
			ctx.moveTo(cx, cy - r);
			ctx.bezierCurveTo(
				cx + r * 1.25,
				cy - r * 0.15,
				cx + r * 0.9,
				cy + r * 0.75,
				cx,
				cy + r * 0.3,
			);
			ctx.bezierCurveTo(
				cx - r * 0.9,
				cy + r * 0.75,
				cx - r * 1.25,
				cy - r * 0.15,
				cx,
				cy - r,
			);
			ctx.closePath();
			ctx.moveTo(cx, cy + r * 0.2);
			ctx.lineTo(cx + r * 0.35, cy + r * 0.95);
			ctx.lineTo(cx - r * 0.35, cy + r * 0.95);
			ctx.closePath();
			ctx.fill("nonzero");
			return;
		case "flag":
			ctx.rect(cx - r * 0.8, cy - r * 0.95, r * 0.2, r * 1.9);
			ctx.moveTo(cx - r * 0.6, cy - r * 0.95);
			ctx.lineTo(cx + r * 0.95, cy - r * 0.48);
			ctx.lineTo(cx - r * 0.6, cy);
			ctx.closePath();
			ctx.fill("nonzero");
			return;
		case "leaf":
			// Two curves bulging in opposite directions, tip to tip on a diagonal.
			ctx.moveTo(cx - r * 0.85, cy + r * 0.85);
			ctx.quadraticCurveTo(
				cx - r * 0.85,
				cy - r * 0.85,
				cx + r * 0.85,
				cy - r * 0.85,
			);
			ctx.quadraticCurveTo(
				cx + r * 0.85,
				cy + r * 0.85,
				cx - r * 0.85,
				cy + r * 0.85,
			);
			ctx.closePath();
			break;
		case "tee":
			points(ctx, cx, cy, r, [
				[-0.9, -0.85],
				[0.9, -0.85],
				[0.9, -0.42],
				[0.22, -0.42],
				[0.22, 0.9],
				[-0.22, 0.9],
				[-0.22, -0.42],
				[-0.9, -0.42],
			]);
			break;
		case "trapezoid":
			points(ctx, cx, cy, r, [
				[-0.5, -0.62],
				[0.5, -0.62],
				[0.95, 0.62],
				[-0.95, 0.62],
			]);
			break;
		case "parallelogram":
			points(ctx, cx, cy, r, [
				[-0.45, -0.62],
				[0.98, -0.62],
				[0.45, 0.62],
				[-0.98, 0.62],
			]);
			break;
		case "cloud":
			for (const [x, y, size] of [
				[-0.5, 0.15, 0.42],
				[0, -0.15, 0.55],
				[0.5, 0.15, 0.42],
			] as const) {
				ctx.moveTo(cx + (x + size) * r, cy + y * r);
				ctx.arc(cx + x * r, cy + y * r, r * size, 0, Math.PI * 2);
			}
			ctx.rect(cx - r * 0.5, cy + r * 0.1, r, r * 0.47);
			ctx.fill("nonzero");
			return;
		case "note":
			// A musical note: an oval head, a stem, and a flag.
			ctx.ellipse(
				cx - r * 0.25,
				cy + r * 0.58,
				r * 0.4,
				r * 0.3,
				-0.35,
				0,
				Math.PI * 2,
			);
			ctx.rect(cx + r * 0.02, cy - r * 0.95, r * 0.18, r * 1.5);
			ctx.moveTo(cx + r * 0.2, cy - r * 0.95);
			ctx.lineTo(cx + r * 0.8, cy - r * 0.55);
			ctx.lineTo(cx + r * 0.8, cy - r * 0.22);
			ctx.lineTo(cx + r * 0.2, cy - r * 0.55);
			ctx.closePath();
			ctx.fill("nonzero");
			return;
		case "trio":
			for (const [x, y] of [
				[0, -0.5],
				[-0.55, 0.42],
				[0.55, 0.42],
			] as const) {
				ctx.moveTo(cx + (x + 0.36) * r, cy + y * r);
				ctx.arc(cx + x * r, cy + y * r, r * 0.36, 0, Math.PI * 2);
			}
			break;
		case "chevron":
			points(ctx, cx, cy, r, [
				[-1, 0.3],
				[0, -0.7],
				[1, 0.3],
				[1, 0.85],
				[0, -0.12],
				[-1, 0.85],
			]);
			break;
	}
	ctx.fill();
}

/**
 * Draw one grid cell of `size` pixels, centered on (`cx`, `cy`): a rounded
 * square in the swatch's color with its icon on top. In the light theme it also
 * gets a thin dark edge, because light colors on a light background would
 * otherwise have no visible edge. Shared by the game's renderer and the
 * palette test page, so both always draw cells the same way.
 */
export function drawTile(
	ctx: CanvasRenderingContext2D,
	swatch: Swatch,
	cx: number,
	cy: number,
	size: number,
	theme: Theme,
): void {
	const inner = size * 0.88;
	ctx.beginPath();
	ctx.roundRect(cx - inner / 2, cy - inner / 2, inner, inner, size * 0.15);
	ctx.fillStyle = swatch.fill;
	ctx.fill();
	if (theme.outline) {
		ctx.strokeStyle = theme.outline;
		ctx.lineWidth = Math.max(1, size * 0.04);
		ctx.stroke();
	}
	ctx.fillStyle = theme.ink;
	drawShape(ctx, swatch.shape, cx, cy, size * 0.26);
}

/**
 * Build a closed outline through a list of points. Each point is given as a
 * fraction of `r` from the center, so (1, 0) is `r` pixels to the right.
 */
function points(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	r: number,
	pts: [number, number][],
): void {
	pts.forEach(([x, y], i) => {
		// `moveTo` first, so each call starts its own outline (see `polygon`).
		if (i === 0) ctx.moveTo(cx + x * r, cy + y * r);
		else ctx.lineTo(cx + x * r, cy + y * r);
	});
	ctx.closePath();
}

/** A teardrop: a round bottom narrowing to a point at the top. */
function drop(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	r: number,
): void {
	const bulb = r * 0.62;
	const center = cy + r * 0.32;
	const tip = cy - r;
	// The point where a straight line from the tip just touches the round
	// bottom, as an angle from straight up (some basic trigonometry).
	const spread = Math.acos(bulb / (center - tip));
	ctx.moveTo(cx, tip);
	ctx.arc(cx, center, bulb, -Math.PI / 2 + spread, (3 * Math.PI) / 2 - spread);
	ctx.closePath();
}

/**
 * A crescent moon: a full circle with an off-center circle cut out of it.
 * The cut is done with a "clip" (an area outside which nothing is drawn),
 * which `save`/`restore` then removes again.
 */
function moon(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	r: number,
): void {
	ctx.save();
	ctx.beginPath();
	ctx.rect(cx - r * 2, cy - r * 2, r * 4, r * 4);
	ctx.arc(cx + r * 0.45, cy - r * 0.2, r * 0.75, 0, Math.PI * 2);
	ctx.clip("evenodd");
	ctx.beginPath();
	ctx.arc(cx, cy, r * 0.95, 0, Math.PI * 2);
	ctx.fill();
	ctx.restore();
}

/**
 * A regular polygon path: `sides` corners spaced evenly around a circle of
 * radius `r` (a triangle, square, hexagon, ...).
 */
function polygon(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	r: number,
	sides: number,
	rotation: number,
): void {
	for (let i = 0; i < sides; i++) {
		const a = rotation + (i * Math.PI * 2) / sides;
		const x = cx + Math.cos(a) * r;
		const y = cy + Math.sin(a) * r;
		// Start with `moveTo` so a second polygon in the same path (the hole of
		// a hollow shape) begins a separate outline instead of joining the first.
		if (i === 0) ctx.moveTo(x, y);
		else ctx.lineTo(x, y);
	}
	ctx.closePath();
}

/** A star path, with points alternating between an `outer` and an `inner` radius. */
function star(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	outer: number,
	inner: number,
	points: number,
): void {
	for (let i = 0; i < points * 2; i++) {
		const r = i % 2 === 0 ? outer : inner;
		const a = -Math.PI / 2 + (i * Math.PI) / points;
		ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
	}
	ctx.closePath();
}

/** A heart path, drawn as two curved halves (bezier curves). */
function heart(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	r: number,
): void {
	const top = cy - r * 0.45;
	ctx.moveTo(cx, cy + r * 0.9);
	ctx.bezierCurveTo(cx - r * 1.3, cy, cx - r * 0.9, top - r * 0.75, cx, top);
	ctx.bezierCurveTo(
		cx + r * 0.9,
		top - r * 0.75,
		cx + r * 1.3,
		cy,
		cx,
		cy + r * 0.9,
	);
	ctx.closePath();
}
